import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import AmbientBackground from '../common/AmbientBackground';
import Button from '../common/Button';
import VoiceInput from '../common/VoiceInput';
import { useReadingsStore } from '../../store/readingsStore';
import { SpreadType } from '../../types';

interface ReadingState {
  question: string;
  cardName: string;
  isReversed: boolean;
  userInterpretation: string;
  aiInterpretation: string;
  advice: string;
  isLoading: boolean;
  error: string | null;
}

const initialState: ReadingState = {
  question: '',
  cardName: '',
  isReversed: false,
  userInterpretation: '',
  aiInterpretation: '',
  advice: '',
  isLoading: false,
  error: null
};

const steps = [
  {
    id: 'welcome',
    title: 'Welcome to Your First Tarot Reading',
    content: (
      <>
        <p className="text-lg text-ink dark:text-parchment/60 mb-4">
          Let's begin your tarot journey with a simple one-card reading using your physical deck. Each card is a mirror of your experience, and we'll help you tune into your intuition as you explore its meaning. There's no right or wrong—just trust what you see and feel.
        </p>

        <p className="text-lg text-ink dark:text-parchment/60 mb-4">
          If you'd prefer to start by learning more about tarot in general, there are some helpful links at the bottom of this page.
        </p>
      </>
    )
  },
  {
    id: 'question',
    title: 'What Would You Like to Know?',
    content: ({ state, setState }: { state: ReadingState; setState: React.Dispatch<React.SetStateAction<ReadingState>> }) => (
      <>
        <p className="text-lg text-ink dark:text-parchment/60 mb-4">
          Before drawing your card, take a moment to center yourself and think about what guidance you're seeking. Your question can be:
        </p>
        <ul className="space-y-2 text-ink dark:text-parchment/60 mb-4">
          <li className="flex items-center">
            <span className="text-moon mr-2">✨</span>
            Open-ended ("What do I need to know about...")
          </li>
          <li className="flex items-center">
            <span className="text-moon mr-2">✨</span>
            About a specific situation or relationship
          </li>
          <li className="flex items-center">
            <span className="text-moon mr-2">✨</span>
            About personal growth or understanding
          </li>
          <li className="flex items-center">
            <span className="text-moon mr-2">✨</span>
            About the energy of the day ahead
          </li>
        </ul>
        <div className="mt-6">
          <label htmlFor="question" className="block text-sm font-display font-medium text-ink dark:text-parchment/60 mb-1">
            Your Question
          </label>
          <div className="relative">
            <textarea
              id="question"
              rows={3}
              className="w-full px-3 py-2 pr-12 border border-mist dark:border-dusk rounded-lg focus:outline-none focus:ring-2 focus:ring-sun/40 dark:focus:ring-ember/40 bg-parchment/60 dark:bg-shadow/60 text-ink dark:text-parchment font-sans placeholder:text-mist"
              value={state.question}
              onChange={(e) => setState(prev => ({ ...prev, question: e.target.value }))}
              placeholder="What would you like guidance about?"
            />
            <div className="absolute right-2 top-2">
              <VoiceInput
                onTranscript={(text) => setState(prev => ({ ...prev, question: prev.question + (prev.question ? ' ' : '') + text }))}
              />
            </div>
          </div>
        </div>
      </>
    )
  },
  {
    id: 'draw',
    title: 'Draw Your Card',
    content: ({ state, setState }: { state: ReadingState; setState: React.Dispatch<React.SetStateAction<ReadingState>> }) => (
      <>
        <p className="text-lg text-ink dark:text-parchment/60 mb-4">
          Now comes the magical part! Take your physical tarot deck and:
        </p>
        <ol className="space-y-4 text-ink dark:text-parchment/60 mb-6">
          <li className="flex items-start">
            <span className="flex-shrink-0 flex items-center justify-center w-6 h-6 rounded-full bg-parchment/60 dark:bg-shadow/60 text-sun dark:text-ember mr-3 mt-0.5">1</span>
            <span>Take a few deep breaths and center yourself</span>
          </li>
          <li className="flex items-start">
            <span className="flex-shrink-0 flex items-center justify-center w-6 h-6 rounded-full bg-parchment/60 dark:bg-shadow/60 text-sun dark:text-ember mr-3 mt-0.5">2</span>
            <span>Shuffle your cards while holding your question in mind</span>
          </li>
          <li className="flex items-start">
            <span className="flex-shrink-0 flex items-center justify-center w-6 h-6 rounded-full bg-parchment/60 dark:bg-shadow/60 text-sun dark:text-ember mr-3 mt-0.5">3</span>
            <span>When it feels right, select one card. Notice if it's upright or reversed (upside down)</span>
          </li>
        </ol>
        <div className="mt-6">
          <label htmlFor="cardName" className="block text-sm font-display font-medium text-ink dark:text-parchment/60 mb-2">
            Which card did you draw?
          </label>
          <div className="relative">
            <input
              type="text"
              id="cardName"
              className="w-full px-3 py-2 pr-12 border border-mist dark:border-dusk rounded-lg focus:outline-none focus:ring-2 focus:ring-sun/40 dark:focus:ring-ember/40 bg-parchment/60 dark:bg-shadow/60 text-ink dark:text-parchment font-sans placeholder:text-mist"
              value={state.cardName}
              onChange={(e) => setState(prev => ({ ...prev, cardName: e.target.value }))}
              placeholder="Enter card name"
            />
            <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
              <VoiceInput
                onTranscript={(text) => setState(prev => ({ ...prev, cardName: text }))}
              />
            </div>
          </div>
          <div className="mt-4">
            <label className="inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={state.isReversed}
                onChange={() => setState(prev => ({ ...prev, isReversed: !prev.isReversed }))}
                className="sr-only peer"
              />
              <div className="relative w-11 h-6 bg-mist peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-mist dark:peer-focus:ring-dusk rounded-full peer dark:bg-dusk peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-vellum after:border-mist after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-parchment/80 peer-checked:bg-sun"></div>
              <span className="ms-2 text-sm font-medium text-ink/50 dark:text-parchment/60">Card is reversed</span>
            </label>
          </div>
        </div>
      </>
    )
  },
  {
    id: 'reflect',
    title: 'Your First Impression',
    content: ({ state, setState }: { state: ReadingState; setState: React.Dispatch<React.SetStateAction<ReadingState>> }) => (
      <>
        <p className="text-lg text-ink dark:text-parchment/60 mb-4">
          Before we explore the traditional meaning, trust your intuition. Look at your card and notice:
        </p>
        <ul className="space-y-3 text-ink dark:text-parchment/60 mb-6">
          <li className="flex items-start">
            <span className="text-moon mr-2">✨</span>
            <span>What immediately catches your attention in the image?</span>
          </li>
          <li className="flex items-start">
            <span className="text-moon mr-2">✨</span>
            <span>What emotions or memories does this card stir up?</span>
          </li>
          <li className="flex items-start">
            <span className="text-moon mr-2">✨</span>
            <span>If this card could speak to you, what would it say?</span>
          </li>
          <li className="flex items-start">
            <span className="text-moon mr-2">✨</span>
            <span>How might this relate to your question?</span>
          </li>
        </ul>
        <div className="mt-6">
          <label htmlFor="userInterpretation" className="block text-sm font-display font-medium text-ink dark:text-parchment/60 mb-2">
            Share your initial thoughts
          </label>
         <div className="relative">
           <textarea
             id="userInterpretation"
             rows={4}
             className="w-full px-3 py-2 pr-12 border border-mist dark:border-dusk rounded-lg focus:outline-none focus:ring-2 focus:ring-sun/40 dark:focus:ring-ember/40 bg-parchment/60 dark:bg-shadow/60 text-ink dark:text-parchment font-sans placeholder:text-mist"
             value={state.userInterpretation}
             onChange={(e) => setState(prev => ({ ...prev, userInterpretation: e.target.value }))}
             placeholder="What do you see in the card? How does it speak to your question?"
           />
           <div className="absolute right-2 top-2">
             <VoiceInput
               onTranscript={(text) => setState(prev => ({ ...prev, userInterpretation: prev.userInterpretation + (prev.userInterpretation ? ' ' : '') + text }))}
             />
           </div>
         </div>
        </div>
      </>
    )
  },
  {
    id: 'ai-interpretation',
    title: 'Enhanced Interpretation',
    content: ({ state, setState }: { state: ReadingState; setState: React.Dispatch<React.SetStateAction<ReadingState>> }) => {
      const handleGenerateInterpretation = async () => {
        setState(prev => ({ ...prev, isLoading: true, error: null }));

        try {
          const { setCurrentReading } = useReadingsStore.getState();

          setCurrentReading({
            subject: 'Guided Reading',
            question: state.question || 'What guidance do the cards have for me?',
            context: `A single card reading exploring: ${state.question || 'What guidance do the cards have for me?'}`,
            spreadType: '1-card' as SpreadType,
            cards: [{
              name: state.cardName,
              position: 'Single Card',
              isReversed: state.isReversed
            }],
            userInterpretation: state.userInterpretation,
            is_virtual: false
          });

          // Navigate to interpretation view
          navigate('/interpretation');
        } catch (error) {
          setState(prev => ({
            ...prev,
            error: error instanceof Error ? error.message : 'Failed to generate interpretation',
            isLoading: false
          }));
        }
      };

      return (
        <>
          <div className="bg-parchment/60 dark:bg-shadow/60 border border-mist dark:border-dusk rounded-xl p-6 mb-6">
            <div className="mb-4">
              <h2 className="text-xl font-display font-medium text-sun dark:text-ember">
                Let's Add Some Magic
              </h2>
            </div>
            <p className="text-ink dark:text-parchment/60 mb-4">
              Now that you've formed your own interpretation, let's see what additional insights our enhanced guide can offer.
            </p>
            {!state.aiInterpretation && (
              <Button
                variant="primary"
                onClick={handleGenerateInterpretation}
                isLoading={state.isLoading}
                disabled={!state.question || !state.cardName}
              >
                Get Interpretation
              </Button>
            )}
          </div>

          {state.error && (
            <div className="bg-sun/10 text-sun p-4 rounded-xl mb-6">
              {state.error}
            </div>
          )}
        </>
      );
    }
  }
];

const GuidedReading: React.FC = () => {
  const navigate = useNavigate();
  const { setCurrentReading } = useReadingsStore();
  const [currentStep, setCurrentStep] = useState(0);
  const [state, setState] = useState<ReadingState>(initialState);

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      navigate('/start');
    }
  };

  const getStepContent = (step: typeof steps[0]) => {
    if (typeof step.content === 'function') {
      return step.content({ state, setState });
    }
    return step.content;
  };

  return (
    <AmbientBackground mood="sun" backgroundImage="/bg-still-mist.png">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="max-w-3xl mx-auto px-4 py-12"
      >
        <div className="mb-8">
          <h1 className="font-display text-3xl md:text-4xl font-semibold text-ink dark:text-parchment">
            {steps[currentStep].title}
          </h1>
        </div>

        <div className="mb-8">
          {getStepContent(steps[currentStep])}
        </div>

        <div className="border-t border-mist dark:border-dusk pt-8 flex flex-col space-y-8">
          <div className="flex justify-between w-full">
            <Button
              variant="outline"
              onClick={() => currentStep > 0 ? setCurrentStep(currentStep - 1) : navigate('/start')}
            >
              Back
            </Button>
            <Button
              variant="primary"
              onClick={handleNext}
              disabled={state.isLoading}
            >
              {currentStep < steps.length - 1 ? 'Next' : 'Return Home'}
            </Button>
          </div>

          {currentStep === 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-parchment/60 dark:bg-shadow/60 border border-mist dark:border-dusk rounded-xl p-6">
                <h3 className="text-xl font-display font-bold text-sun dark:text-ember mb-3">
                  No deck? No problem!
                </h3>
                <Link
                  to="/virtual-deck"
                  className="text-sun dark:text-ember hover:text-sun dark:hover:text-ember font-medium"
                >
                  Visit the Virtual Deck Experience →
                </Link>
              </div>

              <div className="bg-parchment/60 dark:bg-shadow/60 border border-mist dark:border-dusk rounded-xl p-6">
                <h3 className="text-xl font-display font-bold text-sun dark:text-ember mb-3">
                  Looking to do a full reading?
                </h3>
                <Link
                  to="/new-reading"
                  className="text-sun dark:text-ember hover:text-sun dark:hover:text-ember font-medium"
                >
                  Start a New Reading →
                </Link>
              </div>
            </div>
          )}

          <div className="flex justify-center gap-4 pt-4 border-t border-mist dark:border-dusk">
            <Link
              to="/guide/101"
              className="text-sun dark:text-ember hover:text-sun dark:hover:text-ember"
            >
              Tarot 101
            </Link>
            <Link
              to="/guide"
              className="text-sun dark:text-ember hover:text-sun dark:hover:text-ember"
            >
              Tarot Companion
            </Link>
            <Link
              to="/faq"
              className="text-sun dark:text-ember hover:text-sun dark:hover:text-ember"
            >
              About This App
            </Link>
          </div>
        </div>
      </motion.div>
    </AmbientBackground>
  );
};

export default GuidedReading;
